import React, { useState, useEffect, useRef } from "react"
import { useNavigate } from "react-router-dom"
import { motion, AnimatePresence } from "framer-motion"
import { DashboardLayout } from "../../../components/dashboard-layout"
import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card"
import { Progress } from "../../../components/ui/progress"
import { Button } from "../../../components/ui/button"
import { useTheme } from "../../../contexts/theme-provider"; 
import { Alert, AlertDescription } from "../../../components/ui/alert"
import {
  FileSpreadsheet,
  Brain,
  BarChart3,
  CheckCircle,
  AlertCircle,
  ArrowRight,
  Clock,
  Sparkles,
  Zap,
  Target,
  TrendingUp,
} from "lucide-react"

// Define missing variants for framer-motion
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      duration: 0.5
    }
  }
};

interface ReportData {
  reportName: string;
  otherInfo: string;
  fileName: string;
  fileSize: number;
  createdAt: string;
  id: string;
  // Add the fields from your API response
  status?: string;
  download_url?: string;
  downloadUrl?: string;
  input_download_url?: string;
}

interface ProcessingStep {
  id: string
  title: string
  description: string
  icon: React.ComponentType<{ className?: string }>
  completed: boolean
  active: boolean
  details: string[]
}

export default function ReportProcessingPage() {
  const { theme } = useTheme();
  const navigate = useNavigate()

  const [reportData, setReportData] = useState<ReportData | null>(null)
  const [progress, setProgress] = useState(0)
  const [isComplete, setIsComplete] = useState(false)
  const [error, ] = useState("")
  const [estimatedTime, setEstimatedTime] = useState(120)
  const [currentDetail, setCurrentDetail] = useState("")
  const processRef = useRef<HTMLDivElement | null>(null);

  const steps:ProcessingStep[] = [
    {
      id: "upload",
      title: "File Analysis",
      description: "Analyzing uploaded Excel file structure and data",
      icon: FileSpreadsheet,
      completed: false,
      active: true,
      details: [
        "Reading file structure...",
        "Validating data integrity...",
        "Extracting financial metrics...",
        "Identifying data patterns...",
      ],
    },
    {
      id: "ai-processing",
      title: "AI Processing",
      description: "Running advanced AI algorithms on financial data",
      icon: Brain,
      completed: false,
      active: false,
      details: [
        "Initializing AI models...",
        "Processing financial patterns...",
        "Calculating risk assessments...",
        "Generating insights...",
      ],
    },
    {
      id: "report-generation",
      title: "Report Generation",
      description: "Compiling insights and generating comprehensive report",
      icon: BarChart3,
      completed: false,
      active: false,
      details: [
        "Creating visualizations...",
        "Compiling analysis results...",
        "Formatting report layout...",
        "Adding recommendations...",
      ],
    },
    {
      id: "finalization",
      title: "Finalization",
      description: "Finalizing report and preparing for delivery",
      icon: CheckCircle,
      completed: false,
      active: false,
      details: [
        "Quality assurance check...",
        "Optimizing report performance...",
        "Preparing download links...",
        "Report ready for viewing!",
      ],
    },
  ]

  useEffect(() => {
    // 1. Get the initial report data from session storage.
    // This now includes the real download_url from the API.
    const savedReportData = sessionStorage.getItem("currentReport");
    if (savedReportData) {
      const parsedData = JSON.parse(savedReportData);
      setReportData(parsedData);
      
      // 2. If the API call was synchronous and we already have the URL...
      // Check for both download_url (API response) and downloadUrl (legacy)
      const downloadUrl = parsedData.download_url || parsedData.downloadUrl;
      if (downloadUrl) {
        // We can skip the fake timer and finish immediately.
        handleProcessingComplete(parsedData);
      } else {
        // If the API was asynchronous, you would start polling here.
        // For your current synchronous API, we'll just use the fake timer as a visual effect.
        startFakeProgress();
      }
    } else {
      // If no data, something went wrong, go back.
      navigate("/user/dashboard");
    }
  }, [navigate]);

  const startFakeProgress = () => {
    const totalDuration = 5000; // Let's make it a quick 5-second visual effect
    const progressInterval = 50;
    let currentProgress = 0;

    const progressTimer = setInterval(() => {
      // Stop if the component unmounts or if completion is triggered elsewhere
      if (isComplete) {
        clearInterval(progressTimer);
        return;
      }

      currentProgress += 100 / (totalDuration / progressInterval);
      setProgress(Math.min(currentProgress, 99)); // Don't go to 100% until it's really done

      // Update UI elements like before...
      const remainingTime = Math.max(0, (totalDuration / 1000) - (currentProgress / 100) * (totalDuration / 1000));
      setEstimatedTime(Math.floor(remainingTime));
      // ... update steps and details if you want ...

    }, progressInterval);

    // After the API call in `create-report` is done, `reportData` will be set.
    // The `useEffect` will have already called `handleProcessingComplete`.
    // This timer is just for show in an async scenario.
    // In your case, it's almost redundant, but good to have.
    
    // Cleanup function
    return () => clearInterval(progressTimer);
  };

  const handleProcessingComplete = (finalReportData: ReportData) => {
    setIsComplete(true);
    setProgress(100);
    setEstimatedTime(0);
    setCurrentDetail("Report generation complete!");

    // Store the final report object for the details page
    const completedReport = {
      ...finalReportData,
      status: "completed",
      completedAt: new Date().toISOString(),
    };
    sessionStorage.setItem("completedReport", JSON.stringify(completedReport));

    // Navigate to the details page immediately
    navigate(`/user/reports/${finalReportData.id}`);
  };

  useEffect(() => {
    if (processRef.current) {
      processRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, []);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, "0")}`
  }

  return (
    <DashboardLayout>
      <div
        ref={processRef}
        className={`scroll-mt-30 min-h-screen ${theme === "dark" ? "bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900" : "bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50"}`}
      >
        <motion.div className="p-4 md:p-8" variants={containerVariants} initial="hidden" animate="visible">
          <div className="max-w-5xl mx-auto space-y-6 md:space-y-8">
            {/* Header */}
            <motion.div className="text-center space-y-4" variants={itemVariants}>
              <motion.div
                className="flex items-center justify-center gap-3 mb-4"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", stiffness: 300, delay: 0.2 }}
              >
                {isComplete ? (
                  <motion.div
                    className="w-12 h-12 md:w-16 md:h-16 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full flex items-center justify-center"
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: "spring", stiffness: 300 }}
                  >
                    <CheckCircle className="h-6 w-6 md:h-8 md:w-8 text-white" />
                  </motion.div>
                ) : (
                  <motion.div
                    className="w-12 h-12 md:w-16 md:h-16 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center"
                    animate={{ rotate: 360 }}
                    transition={{ duration: 2, repeat: Number.POSITIVE_INFINITY, ease: "linear" }}
                  >
                    <Sparkles className="h-6 w-6 md:h-8 md:w-8 text-white" />
                  </motion.div>
                )}
              </motion.div>
              <h1
                className={`text-3xl md:text-4xl font-bold ${theme === "dark" ? "text-white" : "text-gray-900"}`}
              >
                {isComplete ? "Report Complete!" : "Processing Your Report"}
              </h1>
              <p
                className={`text-lg md:text-xl ${theme === "dark" ? "text-gray-300" : "text-gray-600"}`}
              >
                {isComplete
                  ? "Your AI-powered analysis is ready for viewing"
                  : "Our AI is analyzing your data and generating comprehensive insights"}
              </p>
            </motion.div>

            {/* Progress Overview */}
            <motion.div variants={itemVariants}>
              <Card
                className={`border-0 shadow-lg md:shadow-2xl backdrop-blur-sm overflow-hidden ${theme === "dark" ? "bg-gray-800/50" : "bg-white/70"}`}
              >
                <CardHeader className="text-center pb-4 p-4 md:p-6">
                  <CardTitle
                    className={`text-xl md:text-2xl flex items-center justify-center gap-3 ${theme === "dark" ? "text-white" : "text-gray-900"}`}
                  >
                    {isComplete ? (
                      <>
                        <CheckCircle className="h-5 w-5 md:h-6 md:w-6 text-green-500" />
                        Analysis Complete!
                      </>
                    ) : (
                      <>
                        <motion.div
                          animate={{ rotate: 360 }}
                          transition={{ duration: 2, repeat: Number.POSITIVE_INFINITY, ease: "linear" }}
                        >
                          <Zap className="h-5 w-5 md:h-6 md:w-6 text-blue-500" />
                        </motion.div>
                        AI Processing in Progress
                      </>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4 md:space-y-6 p-4 md:p-6">
                  <div className="space-y-4">
                    <div className="flex justify-between text-base md:text-lg font-semibold">
                      <span className={`${theme === "dark" ? "text-gray-300" : "text-gray-700"}`}>
                        Progress
                      </span>
                      <span className={`${theme === "dark" ? "text-white" : "text-gray-900"}`}>
                        {Math.round(progress)}%
                      </span>
                    </div>
                    <motion.div initial={{ width: 0 }} animate={{ width: "100%" }} transition={{ duration: 0.5 }}>
                      <Progress
                        value={progress}
                        className="h-3 md:h-4 rounded-full [&>div]:bg-gradient-to-r [&>div]:from-blue-500 [&>div]:to-purple-500 [&>div]:transition-all [&>div]:duration-300"
                      />
                    </motion.div>
                  </div>

                  <AnimatePresence mode="wait">
                    {!isComplete && (
                      <motion.div
                        key="processing"
                        className="flex items-center justify-center gap-3 md:gap-4 py-3 md:py-4"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                      >
                        <Clock
                          className={`h-4 w-4 md:h-5 md:w-5 ${theme === "dark" ? "text-blue-400" : "text-blue-600"}`}
                        />
                        <span
                          className={`text-base md:text-lg font-medium ${theme === "dark" ? "text-gray-300" : "text-gray-600"}`}
                        >
                          Estimated time remaining: {formatTime(estimatedTime)}
                        </span>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  <AnimatePresence mode="wait">
                    {currentDetail && (
                      <motion.div
                        key={currentDetail}
                        className={`text-center py-2 px-3 md:py-3 md:px-4 rounded-lg md:rounded-xl ${theme === "dark" ? "bg-blue-900/30" : "bg-blue-50"}`}
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.9 }}
                        transition={{ duration: 0.3 }}
                      >
                        <p
                          className={`font-medium text-sm md:text-base ${theme === "dark" ? "text-blue-300" : "text-blue-700"}`}
                        >
                          {currentDetail}
                        </p>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {isComplete && (
                    <motion.div
                      className="text-center py-3 md:py-4"
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: 0.5 }}
                    >
                      <p className={`text-base md:text-lg font-semibold text-green-500 mb-2`}>
                        Your comprehensive report has been generated successfully!
                      </p>
                      <p
                        className={`text-xs md:text-sm ${theme === "dark" ? "text-gray-400" : "text-gray-600"}`}
                      >
                        Redirecting to report details...
                      </p>
                    </motion.div>
                  )}
                </CardContent>
              </Card>
            </motion.div>

            {/* Report Info */}
            {reportData && (
              <motion.div variants={itemVariants}>
                <Card
                  className={`border-0 shadow-lg md:shadow-xl backdrop-blur-sm ${theme === "dark" ? "bg-gray-800/50" : "bg-white/70"}`}
                >
                  <CardHeader className="p-4 md:p-6">
                    <CardTitle
                      className={`text-lg md:text-xl flex items-center gap-2 md:gap-3 ${theme === "dark" ? "text-white" : "text-gray-900"}`}
                    >
                      <Target className="h-4 w-4 md:h-5 md:w-5" />
                      Report Details
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-4 md:p-6 pt-0">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                      {[
                        { label: "Report Name", value: reportData.reportName },
                        { label: "File Name", value: reportData.fileName },
                        { label: "File Size", value: `${(reportData.fileSize / 1024 / 1024).toFixed(2)} MB` },
                        { label: "Started", value: new Date(reportData.createdAt).toLocaleString() },
                      ].map((item, index) => (
                        <motion.div
                          key={index}
                          className="space-y-1 md:space-y-2"
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: 0.3 + index * 0.1 }}
                        >
                          <p
                            className={`text-xs md:text-sm font-medium ${theme === "dark" ? "text-gray-400" : "text-gray-600"}`}
                          >
                            {item.label}
                          </p>
                          <p
                            className={`font-semibold text-base md:text-lg ${theme === "dark" ? "text-white" : "text-gray-900"}`}
                          >
                            {item.value}
                          </p>
                        </motion.div>
                      ))}
                    </div>
                    {reportData.otherInfo && (
                      <motion.div
                        className="mt-4 md:mt-6 pt-4 md:pt-6 border-t"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.7 }}
                      >
                        <p
                          className={`text-xs md:text-sm font-medium mb-1 md:mb-2 ${theme === "dark" ? "text-gray-400" : "text-gray-600"}`}
                        >
                          Additional Information
                        </p>
                        <p
                          className={`font-medium text-sm md:text-base ${theme === "dark" ? "text-gray-300" : "text-gray-700"}`}
                        >
                          {reportData.otherInfo}
                        </p>
                      </motion.div>
                    )}
                  </CardContent>
                </Card>
              </motion.div>
            )}

            {/* Processing Steps */}
            <motion.div variants={itemVariants}>
              <Card
                className={`border-0 shadow-lg md:shadow-xl backdrop-blur-sm ${theme === "dark" ? "bg-gray-800/50" : "bg-white/70"}`}
              >
                <CardHeader className="p-4 md:p-6">
                  <CardTitle
                    className={`text-lg md:text-xl flex items-center gap-2 md:gap-3 ${theme === "dark" ? "text-white" : "text-gray-900"}`}
                  >
                    <TrendingUp className="h-4 w-4 md:h-5 md:w-5" />
                    Processing Steps
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4 md:p-6 pt-0">
                  <div className="space-y-3 md:space-y-4">
                    {steps.map((step, index) => {
                      const Icon = step.icon
                      return (
                        <motion.div
                          key={step.id}
                          className={`flex items-center gap-4 md:gap-6 p-4 md:p-6 rounded-xl md:rounded-2xl transition-all duration-300 ${
                            step.completed
                              ? theme === "dark"
                                ? "bg-green-900/20 border border-green-500/30"
                                : "bg-green-50 border border-green-200"
                              : step.active
                                ? theme === "dark"
                                  ? "bg-blue-900/20 border border-blue-500/30"
                                  : "bg-blue-50 border border-blue-200"
                                : theme === "dark"
                                  ? "bg-gray-700/30 border border-gray-600/30"
                                  : "bg-gray-50 border border-gray-200"
                          }`}
                          initial={{ opacity: 0, x: -50 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: index * 0.1 }}
                          whileHover={{ scale: 1.02 }}
                        >
                          <motion.div
                            className={`flex-shrink-0 w-12 h-12 md:w-16 md:h-16 rounded-xl md:rounded-2xl flex items-center justify-center ${
                              step.completed
                                ? "bg-gradient-to-r from-green-500 to-emerald-500 text-white"
                                : step.active
                                  ? "bg-gradient-to-r from-blue-500 to-purple-500 text-white"
                                  : theme === "dark"
                                    ? "bg-gray-600 text-gray-300"
                                    : "bg-gray-400 text-white"
                            }`}
                            whileHover={{ rotate: step.active ? 360 : 5 }}
                            transition={{
                              duration: step.active ? 2 : 0.3,
                              repeat: step.active ? Number.POSITIVE_INFINITY : 0,
                              ease: "linear",
                            }}
                          >
                            {step.completed ? (
                              <CheckCircle className="h-6 w-6 md:h-8 md:w-8" />
                            ) : step.active ? (
                              <motion.div
                                animate={{ rotate: 360 }}
                                transition={{ duration: 2, repeat: Number.POSITIVE_INFINITY, ease: "linear" }}
                              >
                                <Icon className="h-6 w-6 md:h-8 md:w-8" />
                              </motion.div>
                            ) : (
                              <Icon className="h-6 w-6 md:h-8 md:w-8" />
                            )}
                          </motion.div>
                          <div className="flex-1">
                            <h4
                              className={`font-bold text-base md:text-xl mb-1 md:mb-2 ${
                                step.completed
                                  ? theme === "dark"
                                    ? "text-green-400"
                                    : "text-green-700"
                                  : step.active
                                    ? theme === "dark"
                                      ? "text-blue-400"
                                      : "text-blue-700"
                                    : theme === "dark"
                                      ? "text-gray-400"
                                      : "text-gray-600"
                              }`}
                            >
                              {step.title}
                            </h4>
                            <p
                              className={`text-sm md:text-base mb-2 md:mb-3 ${
                                step.completed
                                  ? theme === "dark"
                                    ? "text-green-300"
                                    : "text-green-600"
                                  : step.active
                                    ? theme === "dark"
                                      ? "text-blue-300"
                                      : "text-blue-600"
                                    : theme === "dark"
                                      ? "text-gray-500"
                                      : "text-gray-500"
                              }`}
                            >
                              {step.description}
                            </p>
                            {step.active && currentDetail && (
                              <motion.p
                                className={`text-xs md:text-sm font-medium ${theme === "dark" ? "text-blue-200" : "text-blue-800"}`}
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                key={currentDetail}
                              >
                                → {currentDetail}
                              </motion.p>
                            )}
                          </div>
                          {step.active && (
                            <motion.div
                              className="flex-shrink-0"
                              animate={{ scale: [1, 1.2, 1] }}
                              transition={{ duration: 1, repeat: Number.POSITIVE_INFINITY }}
                            >
                              <div className="w-2 h-2 md:w-3 md:h-3 bg-blue-500 rounded-full" />
                            </motion.div>
                          )}
                        </motion.div>
                      )
                    })}
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            {/* Error Handling */}
            {error && (
              <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}>
                <Alert variant="destructive" className="border-red-500/50 bg-red-500/10">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription className="text-red-400">{error}</AlertDescription>
                </Alert>
              </motion.div>
            )}

            {/* Action Buttons */}
            <motion.div className="flex flex-col sm:flex-row justify-center gap-4 md:gap-6" variants={itemVariants}>
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} className="w-full sm:w-auto">
                <Button
                  variant="outline"
                  onClick={() => navigate("/dashboard")}
                  disabled={!isComplete}
                  className={`w-full px-4 py-2 md:px-6 md:py-3 text-sm md:text-base ${theme === "dark" ? "border-gray-600 text-gray-300 hover:bg-gray-700 hover:text-white" : "border-gray-300 text-gray-700 hover:bg-gray-50"}`}
                >
                  Return to Dashboard
                </Button>
              </motion.div>
              {isComplete && reportData && (
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.5 }}
                  className="w-full sm:w-auto"
                >
                  <Button
                    onClick={() => navigate(`/user/reports/${reportData.id}`)}
                    className="w-full px-4 py-2 md:px-8 md:py-3 text-sm md:text-base font-semibold bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white border-0 shadow-lg"
                  >
                    View Report
                    <ArrowRight className="ml-1 md:ml-2 h-4 w-4 md:h-5 md:w-5" />
                  </Button>
                </motion.div>
              )}
            </motion.div>

            {/* Tips */}
            <motion.div variants={itemVariants}>
              <Card
                className={`border-0 shadow-lg md:shadow-xl backdrop-blur-sm overflow-hidden ${theme === "dark" ? "bg-gradient-to-r from-yellow-900/30 to-orange-900/30" : "bg-gradient-to-r from-yellow-50 to-orange-50"}`}
              >
                <CardContent className="p-4 md:p-6">
                  <h4
                    className={`font-bold text-base md:text-lg mb-3 md:mb-4 flex items-center gap-2 ${theme === "dark" ? "text-yellow-400" : "text-yellow-800"}`}
                  >
                    <Sparkles className="h-4 w-4 md:h-5 md:w-5" />
                    While you wait...
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
                    {[
                      "Our AI is analyzing patterns in your financial data",
                      "The system is generating risk assessments and insights",
                      "You can safely close this tab - we'll save your progress",
                      "Check 'Previous Reports' to view completed analyses",
                    ].map((tip, index) => (
                      <motion.div
                        key={index}
                        className={`flex items-center gap-2 md:gap-3 text-xs md:text-sm ${theme === "dark" ? "text-yellow-300" : "text-yellow-700"}`}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.8 + index * 0.1 }}
                      >
                        <div
                          className={`w-1.5 h-1.5 md:w-2 md:h-2 rounded-full flex-shrink-0 ${theme === "dark" ? "bg-yellow-400" : "bg-yellow-600"}`}
                        />
                        {tip}
                      </motion.div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </DashboardLayout>
  )
}